import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { injectIntl } from '@nobia/zeus-components/lib/i18n'
import 'url-search-params-polyfill'
import { withRouter } from '@nobia/zeus-components/lib/router'
import { graphql } from '@nobia/zeus-components/lib/apollo-hoc'
import features from '@nobia/zeus-components/lib/features'
import { PhotoSwipe } from 'react-photoswipe'
import queryString from 'query-string'
import {
  compose,
  branch,
  withHandlers,
  withStateHandlers,
} from '@nobia/zeus-components/lib/recompose'
import { Container } from '@nobia/zeus-components/lib/grid'
import styled, {
  theme,
  withTheme,
  createGlobalStyle,
} from '@nobia/zeus-components/lib/styled'
import { tracker } from '@nobia/zeus-components/lib/tracking'
import { gaListener } from '@nobia/zeus-components/lib/tracking/listeners'

import ContentHeader from '../../components/content-header'
import withGraphqlHandler from '../../components/graphql-handler'
import ImageListQuery from '../../../graphql/ImageListQuery.graphql'
import ImageListBlockQuery from '../../../graphql/ImageListBlockQuery.graphql'

import stateReducer from './reducer'
import { createModalHtml } from './utils'

import { ImageGallery, ImageGalleryMobile } from './image-gallery'
import GalleryFilter from '../../components/filter'
import withAthenaHandler from '../../components/athena-handler'

const imageWidth = features.fullScreen ? '100%' : 'auto'
const cursor = features.fullScreen && 'default'

const MainContainer = styled(Container)`
  margin-top: 38px;
  width: 100%;
  display: flex;
  align-items: ${theme('mainContainer.alignItems')};
  justify-content: center;
  flex-direction: column;
  max-width: ${theme('grid.containerMaxWidth')};
  ${props => props.theme.media.min.md`
    margin-top: 45px;
  `}

  @media (min-width: 1024px) {
    padding-left: ${theme('mainContainer.athenaPaddingTablet')};
    padding-right: ${theme('mainContainer.athenaPaddingTablet')};
  }

  @media (min-width: 1600px) {
    padding-left: ${theme('mainContainer.athenaPaddingDesktop')};
    padding-right: ${theme('mainContainer.athenaPaddingDesktop')};
  }
`

const MobileContainer = styled.div`
  display: none;
  margin-bottom: 60px;
  ${props => props.theme.media.max.xs`
    display: block;
    height: 100%;
    width: 100%;
  `}
`

const OtherDeviceContainer = styled.div`
  display: none;
  width: 100%;
  align-items: center;
  justify-content: center;
  max-width: ${theme('grid.containerMaxWidth')};
  margin-left: auto;
  margin-right: auto;
  ${props => props.theme.media.min.sm`
    display: flex;
  `}
`

const StyledContainer = styled(Container)`
  width: 100%;
  max-width: ${theme('grid.containerMaxWidth')};
  margin-top: 60px;
  margin-bottom: 60px;
`

const StyledRow = styled.div`
  margin: 0 -24px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;

  @media (min-width: 1024px) {
    padding-left: ${theme('row.athenaPaddingTablet')};
    padding-right: ${theme('row.athenaPaddingTablet')};
  }

  @media (min-width: 1600px) {
    padding-left: ${theme('row.athenaPaddingDesktop')};
    padding-right: ${theme('row.athenaPaddingDesktop')};
  }
`

const StyledColumn = styled.div`
  width: 100%;
  max-width: 100%;
  flex: 0 0 100%;
  padding: 0 24px;

  ${props => props.theme.media.min.sm`
    max-width: ${+(100 / props.columns).toFixed(5)}%;
    flex: 0 0 ${+(100 / props.columns).toFixed(5)}%;
    flex-direction: column;
  `}
`

const GroupIfDifferentDesign = styled.div`
  width: 100%;
  flex-direction: column;
`

const BackgroundElement = styled.div`
  position: fixed;
  bottom: 0;
  height: 79px;
  background: #f5f5f5;
  width: 100vw;
  display: ${theme('backgroundElement.display')};
  z-index: 2147483646;
`

const CollectionWrapper = styled.div`
  position: fixed;
  z-index: 2147483647;
  display: flex;
  flex-direction: ${theme('collectionWrapper.flexDirection')};
  align-items: ${theme('collectionWrapper.alignItems')};
  opacity: 0;
  -webkit-animation: labelAnimation 1.4s 0ms 1 normal ease-in-out forwards;
  -moz-animation: labelAnimation 1.4s 0ms 1 normal ease-in-out forwards;
  -o-animation: labelAnimation 1.4s 0ms 1 normal ease-in-out forwards;
  animation: labelAnimation 1.4s 0ms 1 normal ease-in-out forwards;

  /* Safari 4.0 - 8.0 */
  @-webkit-keyframes labelAnimation {
    from {
      margin-left: ${theme('collectionWrapper.labelAnimationFrom.marginLeft')};
      opacity: ${theme('collectionWrapper.labelAnimationFrom.opacity')};
    }
    to {
      margin-left: ${theme('collectionWrapper.labelAnimationTo.marginLeft')};
      opacity: ${theme('collectionWrapper.labelAnimationTo.opacity')};
    }
  }

  /* Standard syntax */
  @keyframes labelAnimation {
    from {
      margin-left: ${theme('collectionWrapper.labelAnimationFrom.marginLeft')};
      opacity: ${theme('collectionWrapper.labelAnimationFrom.opacity')};
    }
    to {
      margin-left: ${theme('collectionWrapper.labelAnimationTo.marginLeft')};
      opacity: ${theme('collectionWrapper.labelAnimationTo.opacity')};
    }
  }
  top: ${theme('collectionWrapper.top')};
  height: ${theme('collectionWrapper.height')};
  left: 0px;
  width: ${theme('collectionWrapper.width')};
  letter-spacing: 0.4px;
  background-color: ${theme('collectionWrapper.backgroundColor')};
  ${props => props.theme.media.min.sm`
    width: ${theme('collectionWrapper.width')};
  `}
`

const NumberOfImages = styled.div`
  position: relative;
  font-size: ${theme('typography.numberOfImages.fontSize')};
  line-height: ${theme('typography.numberOfImages.lineHeight')};
  text-decoration: none;
  text-transform: ${theme('typography.modalHeadline.textTransform')};
  width: 90px;
  height: ${theme('numberOfImages.height')};
  color: ${theme('typography.numberOfImages.color')};
  font-weight: ${theme('typography.numberOfImages.fontWeight')};
  letter-spacing: 0.35px;
  margin: ${theme('numberOfImages.margin')};
  ${props => props.theme.media.min.sm`
    margin: ${theme('numberOfImages.marginDesktop')};
  `}
`

const StyledLink = styled.a`
  color: ${theme('styledLink.color')};
  font-size: ${theme('typography.modalText.fontSize')};
  font-weight: ${theme('styledLink.fontWeight')};
  text-decoration: none;
  margin-left: 3px;
  border-radius: 1px;
  border: ${theme('styledLink.border')};
  border-width: ${theme('styledLink.borderWidth')};
  margin-right: ${theme('styledLink.marginRight')};
  align-items: center;
  justify-content: center;
  display: flex;
  text-align: ${theme('styledLink.textAlign')};
  width: auto;
  white-space: nowrap;
  padding: ${theme('styledLink.padding')};
  ${props => props.theme.media.min.sm`
    margin: ${theme('styledLink.marginRightDesktop')};
  `}
`

const CollectionLabel = styled.div`
  position: relative;
  margin: 3px 0;
  font-size: ${theme('typography.modalHeadline.fontSize')};
  line-height: 34px;
  text-decoration: none;
  font-weight: ${theme('typography.modalHeadline.fontWeight')};
  color: ${theme('typography.modalHeadline.color')};
  text-transform: ${theme('typography.modalHeadline.textTransform')};
  margin: ${theme('collectionLabel.margin')};
  ${props => props.theme.media.min.sm`
    font-size: ${theme('typography.modalHeadline.fontSizeDesktop')};
    line-height: ${theme('collectionLabel.lineHeight')};
    margin: ${theme('collectionLabel.marginDesktop')};
  `}
`

const GlobalStyle = createGlobalStyle`

  body {
      -webkit-font-smoothing: antialiased;
  }
  .c-yellow-header {
    position: initial !important;
    width: 100%;
    z-index: 10;
  }

  .pswp.pswp {
    z-index: 2147483645;
  }

  @media (-webkit-min-device-pixel-ratio: 1.1), (-webkit-min-device-pixel-ratio: 1.09375), (min-resolution: 1.1dppx), (min-resolution: 105dpi) {
    .pswp--svg .pswp__button, .pswp--svg .pswp__button--arrow--left:before, .pswp--svg .pswp__button--arrow--right:before {

    }
  }
  .pswp__button--arrow--left:before {
    left: 6px;
  }
  .pswp__button--arrow--right:before {
    right: 6px;
  }

  .pswp__button--arrow--left:before, .pswp__button--arrow--right:before {
    content: "";
    top: 35px;
    background-color: transparent;
    height: 30px;
    width: 32px;
    position: absolute;
  }

  .pswp__button, .pswp__button--arrow--left:before, .pswp__button--arrow--right:before {
    width: 22px;
    height: 22px;
}

  .pswp__top-bar {
    background-color: transparent;
    opacity: 1 !important;
  }

  .pswp__caption__center {
    text-align: center;
  }

  .pswp__button--close {
    background-position: 0px 0px !important;
    background-color: transparent;
    background-image: url("data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyB3aWR0aD0iMTlweCIgaGVpZ2h0PSIxOXB4IiB2aWV3Qm94PSIwIDAgMTkgMTkiIHZlcnNpb249IjEuMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayI+CiAgICA8dGl0bGU+Y2xvc2U8L3RpdGxlPgogICAgPGcgaWQ9Ikluc3BpcmF0aW9uIiBzdHJva2U9Im5vbmUiIHN0cm9rZS13aWR0aD0iMSIgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIiBzdHJva2UtbGluZWNhcD0icm91bmQiPgogICAgICAgIDxnIGlkPSJHYWxsZXJ5LWltYWdlIiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtMTczOS4wMDAwMDAsIC0zMC4wMDAwMDApIiBzdHJva2U9IiNGRkZGRkYiPgogICAgICAgICAgICA8ZyBpZD0iY2xvc2UiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDE3NDAuMjE3NTE0LCAzMS4yODI0ODYpIj4KICAgICAgICAgICAgICAgIDxsaW5lIHgxPSIyLjI3MzczNjc1ZS0xMyIgeTE9IjAiIHgyPSIxNyIgeTI9IjE3IiBpZD0iTGluZS0yIj48L2xpbmU+CiAgICAgICAgICAgICAgICA8bGluZSB4MT0iMTciIHkxPSIxLjU5MTYxNTczZS0xMiIgeDI9IjAiIHkyPSIxNyIgaWQ9IkxpbmUtMiI+PC9saW5lPgogICAgICAgICAgICA8L2c+CiAgICAgICAgPC9nPgogICAgPC9nPgo8L3N2Zz4=") !important;
    color: #919191;
    right: 35px;
    top: 9px;
    z-index: 9999;
    width: 22px;
    height: 22px;
    background-size: 22px 22px !important;
  }

  .pswp__img {
      position: absolute;
      left: 0;
      min-width: ${imageWidth};
      object-fit: cover;
  }

  .pswp--zoom-allowed .pswp__img {
    cursor: ${cursor} !important;
  }

  .pswp__ui--fit .pswp__caption, .pswp__ui--fit .pswp__top-bar {
      background-color: none !important;
  }

  .pswp__bg {
    background: #FFF;
    opacity: 0.95 !important;
  }

  figure {
    margin: 0 !important;
  }

  img {
    width: 100%;
  }

  figcaption {
    position: absolute;
    bottom: 0;
    left: 0;
    opacity: 0;
  }

  .pswp--open figure img {
    border-style: none;
    opacity: 1;
    width: auto;
    height: 100vh;
  }

  .pswp__container{
      transition:.3s ease-in-out all;

      video {
        width: 100%;
        height: 100%;
    }
  }
`

tracker.addListener(gaListener)

class Gallery extends Component {
  constructor(props) {
    super(props)

    this.state = {
      isOpen: false,
      openDirectLink: false,
      totalItems: 1,
      columns: 3,
      selectedFilters: [],
      showFilters: false,
      imageCollections1:
        (props.galleryProps.pageContent.imageCollections1 &&
          props.galleryProps.pageContent.imageCollections1.expandedValue) ||
        [],
      imageCollections2:
        (props.galleryProps.pageContent.imageCollections2 &&
          props.galleryProps.pageContent.imageCollections2.expandedValue) ||
        [],
      imageCollections3:
        (props.galleryProps.pageContent.imageCollections3 &&
          props.galleryProps.pageContent.imageCollections3.expandedValue) ||
        [],
      filterImageCollections1:
        (props.galleryProps.pageContent.imageCollections1 &&
          props.galleryProps.pageContent.imageCollections1.expandedValue) ||
        [],
      filterImageCollections2:
        (props.galleryProps.pageContent.imageCollections2 &&
          props.galleryProps.pageContent.imageCollections2.expandedValue) ||
        [],
      filterImageCollections3:
        (props.galleryProps.pageContent.imageCollections3 &&
          props.galleryProps.pageContent.imageCollections3.expandedValue) ||
        [],
      mobileImageCollections: [],
      modalItems: [],
      modalCollection: {},
      options: {
        closeEl: true,
        captionEl: false,
        fullscreenEl: false,
        zoomEl: false,
        shareEl: false,
        counterEl: false,
        arrowEl: true,
        preloaderEl: true,
        indexIndicatorSep: ' / ',
        barsSize: { top: 0, bottom: 0, left: 0 },
        galleryUID: 0,
        galleryPIDs: true,
        ...(features.fullScreen && { maxSpreadZoom: 1 }),
        ...(features.fullScreen && { pinchToClose: false }),
        ...(features.fullScreen && {
          getDoubleTapZoom: item => item.initialZoomLevel,
        }),

        getThumbBoundsFn: () => {
          // find thumbnail element
          const thumbnail = document.getElementById(
            this.state.modalCollection.contentLink.id
          )
          if (!thumbnail) {
            return {}
          }
          // get window scroll Y
          const pageYScroll =
            window.pageYOffset || document.documentElement.scrollTop
          // optionally get horizontal scroll

          // get position of element relative to viewport
          const rect = thumbnail.getBoundingClientRect()

          // w = width
          return { x: rect.left, y: rect.top + pageYScroll, w: rect.width }

          // Good guide on how to get element coordinates:
          // http://javascript.info/tutorial/coordinates
        },
      },
    }
  }

  openModalIfDirectLink = () => {
    const location = this.props.location
    const themes = this.props.theme
    const parsedHash = queryString.parse(location.hash)
    if (
      location &&
      location.hash &&
      parsedHash.gid &&
      parsedHash.pid &&
      document &&
      document.documentElement &&
      document.documentElement.clientWidth &&
      themes &&
      themes.breakpoints &&
      themes.breakpoints.xs &&
      themes.breakpoints.xs + 0 <= document.documentElement.clientWidth
    ) {
      const container = document.getElementById(parsedHash.gid)
      if (container && container.hasAttribute('data-count')) {
        this.openModal(container.getAttribute('data-count'), parsedHash.gid)
      }
    }
  }

  handleClose = () => {
    const body = document.querySelector('body')
    const classList = body && body.classList
    if (classList && classList.contains('gallery-modal--open')) {
      body.classList.remove('gallery-modal--open')
    }

    this.setState({
      isOpen: false,
    })
  }

  gettingData = (item, index) => {
    if (this.state.isOpen) {
      if (
        item.getCurrentIndex() + 1 !== this.props.currentNr &&
        index + 1 !== this.props.currentNr
      ) {
        setTimeout(() => {
          this.props.currentNrToShowFunc(item.getCurrentIndex() + 1)
          if (item.items && item.items.length === 0) {
            const selectedCollection = this.getImageCollection(
              this.state.options.galleryUID,
              this.state.imageCollections1,
              this.state.imageCollections2,
              this.state.imageCollections3
            )
            this.setState({
              modalItems: createModalHtml(selectedCollection),
              modalCollection: selectedCollection,
            })
          }
        }, 0)
      }
    }
  }

  openModal = (numberOfImages, collectionId, directLink = false) => {
    if (directLink && this.state.openDirectLink) return

    if (!this.state.isOpen) {
      const selectedCollection = this.getImageCollection(
        collectionId,
        this.state.imageCollections1,
        this.state.imageCollections2,
        this.state.imageCollections3
      )

      const body = document.querySelector('body')
      const classList = body && body.classList
      if (!classList.contains('gallery-modal--open')) {
        body.classList.add('gallery-modal--open')
      }

      this.setState({
        isOpen: true,
        totalItems: numberOfImages,
        openDirectLink: directLink,
        options: { ...this.state.options, galleryUID: collectionId },
        modalItems: createModalHtml(selectedCollection),
        modalCollection: selectedCollection,
      })
    }
  }

  mergeCollections = (c1, c2, c3) => {
    if (!c1.length && !c2.length && !c3.length) {
      return []
    }
    return [
      c1[0],
      c2[0],
      c3[0],
      ...this.mergeCollections(c1.slice(1), c2.slice(1), c3.slice(1)),
    ].filter(f => f)
  }

  toggleFilter = (filterItem = '') => {
    if (filterItem) {
      if (this.state.selectedFilters.some(f => f === filterItem)) {
        this.state.selectedFilters = this.state.selectedFilters.filter(
          f => f !== filterItem
        )
      } else {
        this.state.selectedFilters.push(filterItem)
      }
    }

    const imageCollections1 = this.filterImageCollections(
      this.state.imageCollections1
    )

    const imageCollections2 = this.filterImageCollections(
      this.state.imageCollections2
    )

    const imageCollections3 = this.filterImageCollections(
      this.state.imageCollections3
    )

    const columns = [
      imageCollections1,
      imageCollections2,
      imageCollections3,
    ].filter(i => i.length).length

    this.setState({
      columns,
      filterImageCollections1: imageCollections1,
      filterImageCollections2: imageCollections2,
      filterImageCollections3: imageCollections3,
      mobileImageCollections: this.mergeCollections(
        imageCollections1,
        imageCollections2,
        imageCollections3
      ),
    })
  }

  filterImageCollections = imageCollections => {
    if (
      this.state.selectedFilters &&
      this.state.selectedFilters.length > 0 &&
      imageCollections.length > 0
    ) {
      return imageCollections.filter(collection => {
        let matchingValues = 0
        if (!collection.filterCategories.expandedValue) {
          return false
        }
        collection.filterCategories.expandedValue.forEach(filterCategory => {
          const included = this.state.selectedFilters.includes(
            filterCategory.contentLink.id
          )
          if (included) {
            matchingValues += 1
          }
          return included
        })
        return matchingValues === this.state.selectedFilters.length
      })
    }
    return imageCollections
  }

  getImageCollection = (
    collectionId,
    imageCollections1,
    imageCollections2,
    imageCollections3
  ) => {
    let imageCollection = imageCollections1.find(
      collection => collection.contentLink.id === collectionId
    )

    if (!imageCollection) {
      imageCollection = imageCollections2.find(
        collection => collection.contentLink.id === collectionId
      )
    }

    if (!imageCollection) {
      imageCollection = imageCollections3.find(
        collection => collection.contentLink.id === collectionId
      )
    }

    return imageCollection
  }

  componentDidMount() {
    this.toggleFilter()
  }

  render() {
    const {
      getString,
      galleryProps: { pageContent },
      currentNr,
    } = this.props
    const {
      modalItems,
      modalCollection,
      isOpen,
      totalItems,
      options,
      selectedFilters,
      filterImageCollections1,
      filterImageCollections2,
      filterImageCollections3,
      mobileImageCollections,
    } = this.state
    return (
      <>
        <GlobalStyle />
        {((pageContent.heading && pageContent.heading.value) ||
          (pageContent.preamble && pageContent.preamble.value)) && (
          <MainContainer>
            <ContentHeader content={pageContent} />
          </MainContainer>
        )}
        {pageContent &&
          pageContent.filters &&
          pageContent.filters.expandedValue &&
          pageContent.filters.expandedValue.length > 0 && (
            <MainContainer>
              <GalleryFilter
                filterLabel={getString(
                  'filter',
                  'Filter',
                  pageContent.language.name
                )}
                selectedFilters={selectedFilters}
                filters={pageContent.filters.expandedValue}
                onClickFilter={id => this.toggleFilter(id)}
              />
            </MainContainer>
          )}

        <OtherDeviceContainer>
          <StyledContainer>
            <StyledRow columns={this.state.columns}>
              {filterImageCollections1.length > 0 && (
                <StyledColumn columns={this.state.columns}>
                  <ImageGallery
                    imageCollections={filterImageCollections1}
                    className="my-gallery-thumbnails"
                    openModalFunc={this.openModal}
                    language={pageContent.language.name}
                    location={this.props.location}
                    themes={this.props.theme}
                  />
                </StyledColumn>
              )}
              {filterImageCollections2.length > 0 && (
                <StyledColumn columns={this.state.columns}>
                  <ImageGallery
                    imageCollections={filterImageCollections2}
                    className="my-gallery-thumbnails2"
                    openModalFunc={this.openModal}
                    language={pageContent.language.name}
                    location={this.props.location}
                    themes={this.props.theme}
                  />
                </StyledColumn>
              )}
              {filterImageCollections3.length > 0 && (
                <StyledColumn columns={this.state.columns}>
                  <ImageGallery
                    imageCollections={filterImageCollections3}
                    className="my-gallery-thumbnails3"
                    openModalFunc={this.openModal}
                    language={pageContent.language.name}
                    location={this.props.location}
                    themes={this.props.theme}
                  />
                </StyledColumn>
              )}
            </StyledRow>
          </StyledContainer>
          <PhotoSwipe
            isOpen={isOpen}
            items={modalItems}
            options={options}
            onClose={this.handleClose}
            id="PhotoSwipe"
            gettingData={this.gettingData}
          />
          {isOpen && (
            <>
              <CollectionWrapper>
                <GroupIfDifferentDesign>
                  <NumberOfImages>
                    {currentNr}/{totalItems}
                  </NumberOfImages>
                  <CollectionLabel>
                    {modalCollection.title.value}
                  </CollectionLabel>
                </GroupIfDifferentDesign>
                {modalCollection.linkText.value && (
                  <StyledLink href={modalCollection.link.value || ''}>
                    {modalCollection.linkText.value}
                  </StyledLink>
                )}
              </CollectionWrapper>
              <BackgroundElement />
            </>
          )}
        </OtherDeviceContainer>

        <MobileContainer>
          <ImageGalleryMobile imageCollections={mobileImageCollections} />
        </MobileContainer>
      </>
    )
  }
}

Gallery.propTypes = {
  currentNr: PropTypes.number.isRequired,
  location: PropTypes.shape({
    hash: PropTypes.string,
  }).isRequired,
  theme: PropTypes.shape({
    breakpoints: PropTypes.shape({
      xs: PropTypes.number,
    }),
  }).isRequired,
  currentNrToShowFunc: PropTypes.func.isRequired,
  galleryProps: PropTypes.shape({
    pageContent: PropTypes.shape({
      imageCollections1: PropTypes.shape(),
      imageCollections2: PropTypes.shape(),
      imageCollections3: PropTypes.shape(),
      language: PropTypes.shape({
        name: PropTypes.string,
      }),
      filters: PropTypes.shape(),
      heading: PropTypes.shape(),
      preamble: PropTypes.shape(),
    }),
  }).isRequired,
  getString: PropTypes.func.isRequired,
}

Gallery.defaultProps = {
  embedBlockId: null,
}
let enhance
if (window.zeus.settings.brand === 'unoform') {
  enhance = compose(
    injectIntl,
    withRouter,
    withTheme,
    withAthenaHandler,
    withGraphqlHandler,
    stateReducer,
    withStateHandlers(
      () => ({
        currentNr: 1,
      }),
      {
        currentNrToShowFunc: () => nr => ({
          currentNr: nr,
        }),
      }
    ),
    withHandlers({
      getString: ({ intl }) => (key, defaultMessage, language) =>
        intl.formatMessage({
          id: `app.${language}.${key}`,
          defaultMessage,
        }),
    })
  )
} else {
  enhance = compose(
    injectIntl,
    withRouter,
    withTheme,
    branch(
      props => props.embedBlockId == null,
      graphql(ImageListQuery, {
        options: () => ({
          variables: {
            path: window.location.pathname.substring(1),
          },
        }),
      }),
      graphql(ImageListBlockQuery, {
        options: props => ({
          variables: {
            pageId: props.embedBlockId,
          },
        }),
      })
    ),
    withGraphqlHandler,
    stateReducer,
    withStateHandlers(
      () => ({
        currentNr: 1,
      }),
      {
        currentNrToShowFunc: () => nr => ({
          currentNr: nr,
        }),
      }
    ),
    withHandlers({
      getString: ({ intl }) => (key, defaultMessage, language) =>
        intl.formatMessage({
          id: `app.${language}.${key}`,
          defaultMessage,
        }),
    })
  )
}

export default enhance(withRouter(Gallery))
